import React, { useState, useEffect, useRef } from "react";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import "../VerifyEmailAP/VerifyEmail.css";
import { useSearchParams } from "react-router-dom";
import { API_URL } from "../../constant";

const VerifyEmail = () => {
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [timeLeft, setTimeLeft] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const inputRefs = useRef([]);
  const [searchParams] = useSearchParams();
  const purpose = searchParams.get("purpose") || "email_verification";
  const email = localStorage.getItem("reset_email") || "";

  useEffect(() => { if (inputRefs.current[0]) inputRefs.current[0].focus(); }, []);

  useEffect(() => {
    if (timeLeft === 0) { setIsResendDisabled(false); return; }
    const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);

  const handleChange = (index, e) => {
    const value = e.target.value;
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1].focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) inputRefs.current[index - 1].focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (isNaN(pastedData)) return;
    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) { if (i < 6) newOtp[i] = pastedData[i]; }
    setOtp(newOtp);
    inputRefs.current[Math.min(pastedData.length, 5)].focus();
  };

  const handleResend = async () => {
    if (isResendDisabled) return;
    setIsLoading(true);
    setMessage({ text: "", type: "" });
    try {
      const endpoint = purpose === "password_reset" ? "/api/member/resend-password-reset" : "/api/member/resend-email-verification";
      const response = await fetch(`${API_URL}${endpoint}`, { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ email }) });
      const data = await response.json();
      if (response.ok) { setTimeLeft(60); setIsResendDisabled(true); setOtp(new Array(6).fill("")); if (inputRefs.current[0]) inputRefs.current[0].focus(); setMessage({ text: "Verification code has been resent to your email", type: "success" }); }
      else { setMessage({ text: data.message || "Failed to resend code.", type: "error" }); }
    } catch (error) { setMessage({ text: "Failed to resend code.", type: "error" }); }
    finally { setIsLoading(false); }
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  const handleVerify = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 6) { setMessage({ text: "Please enter the complete 6-digit code", type: "error" }); return; }
    setIsLoading(true);
    setMessage({ text: "", type: "" });
    try {
      const endpoint = purpose === "password_reset" ? "/api/member/verify-password-otp" : "/api/member/verify-email";
      const response = await fetch(`${API_URL}${endpoint}`, { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ email, otp: enteredOtp }) });
      const data = await response.json();
      if (response.ok) {
        setMessage({ text: purpose === "password_reset" ? "OTP verified successfully!" : "Email verified successfully!", type: "success" });
        if (purpose === "password_reset") { localStorage.setItem("reset_otp", enteredOtp); if (data.reset_token) localStorage.setItem("reset_token", data.reset_token); }
        setTimeout(() => { window.location.href = purpose === "password_reset" ? "/create-new-password-m" : "/member-login"; }, 1500);
      } else { setMessage({ text: data.message || "Invalid verification code.", type: "error" }); }
    } catch (error) { setMessage({ text: "An error occurred.", type: "error" }); }
    finally { setIsLoading(false); }
  };

  const msgStyle = { textAlign: "center", padding: "10px", borderRadius: "8px", margin: "20px 0", fontWeight: "500" };

  return (
    <div className="verify-email-container">
      <Header />
      <div className="verify-email-content">
        <div className="verify-email-card">
          <h2 className="verify-email-heading">Verify your email</h2>
          <p className="verify-email-subheading">Please enter the 6 digit code sent to your email address to verify</p>
          {message.text && (<div style={{ ...msgStyle, backgroundColor: message.type === "error" ? "#ffebee" : "#e8f5e9", color: message.type === "error" ? "#c62828" : "#2e7d32" }}>{message.text}</div>)}
          <div className="otp-container">
            {otp.map((digit, index) => (<input key={index} type="text" inputMode="numeric" pattern="[0-9]*" className="otp-input" value={digit} onChange={(e) => handleChange(index, e)} onKeyDown={(e) => handleKeyDown(index, e)} onPaste={index === 0 ? handlePaste : null} ref={(ref) => (inputRefs.current[index] = ref)} maxLength={255} disabled={isLoading} />))}
          </div>
          <div className="resend-container">
            <div className="timer-container">{isResendDisabled && (<span className="timer-text">Time remaining: {formatTime(timeLeft)}s</span>)}</div>
            <div className="resend-link-container"><span className="resend-prompt">Didn't get the code? </span><button className={`resend-button ${isResendDisabled ? "disabled" : ""}`} onClick={handleResend} disabled={isResendDisabled || isLoading}>{isLoading ? "Sending..." : "Resend"}</button></div>
          </div>
          <div className="verify-button-container"><button className="verify-button" onClick={handleVerify} disabled={isLoading} style={{ opacity: isLoading ? 0.7 : 1, cursor: isLoading ? "not-allowed" : "pointer" }}>{isLoading ? "Verifying..." : "Verify"}</button></div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default VerifyEmail;
